import React, { useState, useEffect } from 'react';
import GrantitudeSpiral from './GrantitudeSpiral';

/**
 * SpiralOrg "What Is This" Page - Page 2 of flow
 * Motion graphic blocks with futuristic glass panels
 * Scroll-triggered animations for digestibility
 */
const SpiralOrgWhatIsThis = ({ onProceedToCinema }) => {
  const [spiralPhase, setSpiralPhase] = useState(0);

  // Grant spiral animation - ULTRA SUBTLE
  useEffect(() => {
    const spiralInterval = setInterval(() => {
      setSpiralPhase(prev => (prev + 0.15) % 360);
    }, 50);
    return () => clearInterval(spiralInterval);
  }, []);

  // Scroll animation effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollElements = document.querySelectorAll('.scroll-block');
      scrollElements.forEach(el => {
        const rect = el.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight * 0.85;
        if (isVisible) {
          el.classList.add('block-animate-in');
        }
      });
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden relative">
      
      {/* ULTRA SUBTLE Grant spiral - opacity 0.05 */}
      <div className="fixed inset-0 pointer-events-none opacity-5" style={{ zIndex: 1 }}>
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{
            transform: `scale(8) rotate(${spiralPhase}deg)`,
            filter: 'blur(4px) brightness(1.3) saturate(1.5)',
            mixBlendMode: 'overlay'
          }}
        >
          <GrantitudeSpiral size={300} />
        </div>
      </div>

      {/* Glass Panel Animations CSS */}
      <style>{`
        .glass-block {
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.03));
          backdrop-filter: blur(20px) saturate(180%);
          border: 1px solid rgba(255, 255, 255, 0.15);
          box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.5), 
                      0 0 0 1px rgba(255, 255, 255, 0.05) inset;
        }
        
        .scroll-block {
          opacity: 0;
          transform: translateY(40px) scale(0.95);
          transition: all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        
        .scroll-block.block-animate-in {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
        
        .scroll-block:hover {
          transform: translateY(-5px) scale(1.02);
          box-shadow: 0 12px 40px 0 rgba(230, 198, 133, 0.2);
        }
      `}</style>

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 py-16">
        
        {/* Title */}
        <h1 
          className="text-5xl md:text-6xl lg:text-7xl font-bold text-center mb-24 scroll-block"
          style={{
            fontFamily: "'Cinzel', serif",
            textShadow: '0 0 30px rgba(230, 198, 133, 0.4)',
            letterSpacing: '0.15em',
            color: '#E6C794'
          }}
        >
          What Is SpiralOrg?
        </h1>

        {/* Opening - Glass Block */}
        <div className="glass-block scroll-block rounded-3xl p-10 mb-12 text-center">
          <p 
            className="text-2xl md:text-3xl font-light leading-relaxed mb-6"
            style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.05em' }}
          >
            SpiralOrg is an organization, an organizer, and an organism.
          </p>
          <p 
            className="text-xl md:text-2xl leading-relaxed text-white/90"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            It's a lawful Chapter 184 Wisconsin association guild of families, working together to remember how to thrive.
          </p>
        </div>

        {/* Mission - Glass Block */}
        <div className="glass-block scroll-block rounded-3xl p-10 mb-12">
          <p 
            className="text-lg md:text-xl leading-relaxed text-white/90 mb-6"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            At its heart, SpiralOrg is a <span className="font-semibold text-white">trade and trust guild</span> where families help each other thrive to maximum capacity.
          </p>
        </div>

        {/* Three Practices - Individual Glass Blocks */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          <div className="glass-block scroll-block rounded-2xl p-8">
            <div className="text-5xl mb-4 text-center">🩹</div>
            <h3 className="text-xl font-semibold text-white mb-3 text-center" style={{ fontFamily: "'Cinzel', serif" }}>
              Healing
            </h3>
            <p className="text-base text-white/80 leading-relaxed text-center" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Remedying tension within families, friends, and teams.
            </p>
          </div>

          <div className="glass-block scroll-block rounded-2xl p-8" style={{ transitionDelay: '0.1s' }}>
            <div className="text-5xl mb-4 text-center">📖</div>
            <h3 className="text-xl font-semibold text-white mb-3 text-center" style={{ fontFamily: "'Cinzel', serif" }}>
              Lore Library
            </h3>
            <p className="text-base text-white/80 leading-relaxed text-center" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Every family creates, modifies, and curates its living story.
            </p>
          </div>

          <div className="glass-block scroll-block rounded-2xl p-8" style={{ transitionDelay: '0.2s' }}>
            <div className="text-5xl mb-4 text-center">🤝</div>
            <h3 className="text-xl font-semibold text-white mb-3 text-center" style={{ fontFamily: "'Cinzel', serif" }}>
              Collaboration
            </h3>
            <p className="text-base text-white/80 leading-relaxed text-center" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Building projects that serve both family and planet.
            </p>
          </div>
        </div>

        {/* The Mechanism - Glass Block */}
        <div className="glass-block scroll-block rounded-3xl p-10 mb-12">
          <h2 
            className="text-3xl md:text-4xl font-light text-center mb-6"
            style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.1em' }}
          >
            The Mechanism
          </h2>
          
          <p className="text-lg md:text-xl leading-relaxed text-white/90 mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            The root mechanism of everything may be thought of as the <span className="font-semibold text-white">intersection of spiraling energy between us</span>. That's where reality happens—where intention, love, and trust meet and create.
          </p>
          
          <p className="text-lg leading-relaxed text-white/80" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            We recruit families for family reunion, because by reuniting the family, we reunite the whole planetary family.
          </p>
        </div>

        {/* Credit Declaration - Glass Block */}
        <div className="glass-block scroll-block rounded-3xl p-10 mb-12 text-center bg-gradient-to-br from-amber-500/10 to-transparent">
          <p className="text-2xl md:text-3xl font-medium text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            We give each other <span className="font-bold text-amber-300">credit</span>—amazing credit.
          </p>
          <p className="text-lg text-white/80" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Not theoretical, but real: the kind that builds trust, wealth, and belonging.
          </p>
        </div>

        {/* SpiralCred - Glass Block */}
        <div className="glass-block scroll-block rounded-3xl p-10 mb-12">
          <h2 
            className="text-3xl md:text-4xl font-light text-center mb-6"
            style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.1em' }}
          >
            SpiralCred
          </h2>
          
          <p className="text-lg md:text-xl leading-relaxed text-white/90 mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Our simple, ingenious accounting system for person-to-person barter. Trade goods, time, and skills without middlemen or extraction.
          </p>
          
          <div className="grid md:grid-cols-2 gap-6 my-8">
            <div className="bg-white/5 rounded-xl p-6">
              <p className="text-base text-white/80 leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Every exchange—repairing a fence, hosting a meal, writing a song—is recorded as <span className="font-semibold text-white">trust earned and trust given</span>.
              </p>
            </div>
            <div className="bg-white/5 rounded-xl p-6">
              <p className="text-base text-white/80 leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                No interest, no scarcity, no outside interference. <span className="font-semibold text-white">Only reciprocity</span>.
              </p>
            </div>
          </div>
          
          <p className="text-xl text-white font-medium text-center" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Lawful, private, regenerative—an economy built on relationship.
          </p>
        </div>

        {/* The Structure - 3 Glass Blocks */}
        <h2 
          className="text-3xl md:text-4xl font-light text-center mb-12 scroll-block"
          style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.1em' }}
        >
          The Structure
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          <div className="glass-block scroll-block rounded-2xl p-8">
            <div className="text-5xl mb-4 text-center">🏛️</div>
            <h3 className="text-xl font-semibold text-white mb-3 text-center" style={{ fontFamily: "'Cinzel', serif" }}>
              Organization
            </h3>
            <p className="text-base text-white/80 leading-relaxed text-center" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Lawful under Wisconsin Chapter 184
            </p>
          </div>

          <div className="glass-block scroll-block rounded-2xl p-8" style={{ transitionDelay: '0.1s' }}>
            <div className="text-5xl mb-4 text-center">⚙️</div>
            <h3 className="text-xl font-semibold text-white mb-3 text-center" style={{ fontFamily: "'Cinzel', serif" }}>
              Organizer
            </h3>
            <p className="text-base text-white/80 leading-relaxed text-center" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Platform to find the right people and complete hidden spirals
            </p>
          </div>

          <div className="glass-block scroll-block rounded-2xl p-8" style={{ transitionDelay: '0.2s' }}>
            <div className="text-5xl mb-4 text-center">🌱</div>
            <h3 className="text-xl font-semibold text-white mb-3 text-center" style={{ fontFamily: "'Cinzel', serif" }}>
              Organism
            </h3>
            <p className="text-base text-white/80 leading-relaxed text-center" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Living federation turning in a virtuous spiral
            </p>
          </div>
        </div>

        {/* The Invitation - Small Digestible Chunks */}
        <div className="glass-block scroll-block rounded-3xl p-10 mb-12">
          <h2 
            className="text-3xl md:text-4xl font-light text-center mb-8"
            style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.1em' }}
          >
            The Invitation
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <div className="bg-white/5 rounded-xl p-5">
              <p className="text-lg text-white/90" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Join the guild
              </p>
            </div>
            <div className="bg-white/5 rounded-xl p-5">
              <p className="text-lg text-white/90" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Help your family heal and thrive
              </p>
            </div>
            <div className="bg-white/5 rounded-xl p-5">
              <p className="text-lg text-white/90" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Trade in trust
              </p>
            </div>
            <div className="bg-white/5 rounded-xl p-5">
              <p className="text-lg text-white/90" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Build your family's lore
              </p>
            </div>
          </div>

          <p 
            className="text-2xl md:text-3xl font-light italic text-center text-white/90 mt-8"
            style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.08em' }}
          >
            The lawful, living guild of families<br/>turning the world toward life.
          </p>
        </div>

        {/* Continue Button - Glass Style */}
        <div className="text-center mt-20 mb-12 scroll-block">
          <button
            onClick={onProceedToCinema}
            className="glass-block px-12 py-5 hover:bg-white/15 border-2 border-amber-500/40 hover:border-amber-500/70 text-white rounded-2xl text-lg transition-all hover:scale-105 shadow-2xl"
            style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.1em' }}
          >
            Explore Directory →
          </button>
        </div>
      </div>
    </div>
  );
};

export default SpiralOrgWhatIsThis;
